/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { ROUTES } from '@futrli/shared-services/app-core';
import { usePredictionsInfoContext } from '@futrli/predict/forecast/detail/context/predictions-info-context';
import { excelFilesQueryKey } from '@futrli/views/queries/excelFiles';
import { queryClient } from '@futrli/views/core/queryClient';

import { UploadEntity } from './upload-entity';
import { useUploadForecast } from '../hooks/use-upload-forecast';
import { ExcelForecastCreatePayload } from '../../types';
import { ExcelUploadDrawer } from './excel-upload-drawer';

export interface ForecastUploadDrawerInnerProps {
  isLoading?: boolean;
  children: JSX.Element;
  handleClose: () => void;
  maxFileSize?: number; // in MB
  acceptedFileExtensions?: string;
  workspaceId: string;
  organisationId: string;
  isUpdateDrawer?: boolean;
  isPredictionsPage?: boolean;
  forecastData: ExcelForecastCreatePayload;
}

export const ForecastUploadDrawerInner = ({
  children,
  handleClose,
  workspaceId,
  organisationId,
  maxFileSize = 25,
  acceptedFileExtensions = '.xls,.xlsx',
  isUpdateDrawer = false,
  forecastData,
  isLoading,
  isPredictionsPage = false
}: ForecastUploadDrawerInnerProps) => {
  const [file, setFile] = useState<File>();

  const { pathname } = useLocation();

  const { fetchForecast, fetchForecastReport } = usePredictionsInfoContext();

  const navigate = useNavigate();

  const {
    createForecastFromExcelFile,
    updateForecastFromExcelFile,
    uploadFailed,
    uploadSuccess,
    setUploadSuccess,
    isInProgress,
    step,
    setStep,
    errorMessage,
    setErrorMessage,
    warningMessage,
    setWarningMessage,
    createdForecastId
  } = useUploadForecast(workspaceId, organisationId);

  useEffect(() => {
    if (uploadSuccess && forecastData.forecast_id) {
      queryClient.invalidateQueries({
        queryKey: excelFilesQueryKey(workspaceId, organisationId, { forecast_id: forecastData.forecast_id })
      });
    }
  }, [uploadSuccess, workspaceId, organisationId, forecastData.forecast_id]);

  useEffect(() => {
    if (file && !isInProgress && step === 'ready') {
      if (!isUpdateDrawer) {
        createForecastFromExcelFile(file, forecastData);
      } else {
        updateForecastFromExcelFile(file, forecastData);
      }
    }
  }, [
    file,
    isInProgress,
    workspaceId,
    createForecastFromExcelFile,
    updateForecastFromExcelFile,
    step,
    isUpdateDrawer,
    forecastData
  ]);

  const onFileChange = useCallback((file: File) => {
    setFile(file);
  }, []);

  useEffect(() => {
    if (uploadFailed) {
      setFile(undefined);
      setStep('ready');
    }
  }, [uploadFailed, setStep]);

  useEffect(() => {
    if (uploadSuccess) {
      setFile(undefined);
      setStep('ready');
    }
  }, [uploadSuccess, setStep]);

  const onCancel = useCallback(() => {
    setFile(undefined);
    setErrorMessage('');
    setWarningMessage('');
    setStep('ready');
    setUploadSuccess(false);
    handleClose();
  }, [handleClose, setErrorMessage, setStep, setUploadSuccess, setWarningMessage]);

  const onFinish = useCallback(async () => {
    // When creating forecasting, we need to have `createdForecastId` for redirect
    if (!createdForecastId && !isUpdateDrawer) {
      return;
    }

    const forecastIdToUse = isUpdateDrawer ? forecastData.forecast_id : createdForecastId;

    if (!forecastIdToUse) {
      return;
    }

    await fetchForecast?.(undefined, forecastIdToUse);
    await fetchForecastReport?.('all', undefined, forecastIdToUse);

    if (isUpdateDrawer || isPredictionsPage) {
      onCancel();
    } else {
      onCancel();
      navigate(`${pathname}/${createdForecastId}${ROUTES.PREDICTIONS}${ROUTES.SALES}`);
    }
  }, [
    createdForecastId,
    isPredictionsPage,
    isUpdateDrawer,
    forecastData.forecast_id,
    fetchForecast,
    fetchForecastReport,
    onCancel,
    navigate,
    pathname
  ]);

  return (
    <ExcelUploadDrawer
      maxFileSize={maxFileSize}
      acceptedFileExtensions={acceptedFileExtensions}
      onCancel={onCancel}
      isInProgress={isInProgress}
      uploadSuccess={uploadSuccess}
      uploadFailed={uploadFailed}
      file={file}
      errorMessage={errorMessage}
      warningMessage={warningMessage}
      step={step}
      entity={UploadEntity.Forecast}
      onFileChange={onFileChange}
      onFinish={onFinish}
      isLoading={isLoading}
      isUpdateDrawer={isUpdateDrawer}
      workspaceId={workspaceId}
      organisationId={organisationId}
      historyConfig={{ entityType: 'forecast', forecastId: forecastData.forecast_id || '' }}
    >
      {children}
    </ExcelUploadDrawer>
  );
};
